import { useState } from 'react';
import { ChevronDown, Lock } from 'lucide-react';
import { Button, Select } from '../ui';

import { has, expandPage, limitOf, actionLabel, ordered, explicitMap, type PermissionsMap, type Registry, type RegistryPage, type PermissionTemplate } from './permissions';

export type { PermissionsMap, Registry, RegistryPage, PermissionTemplate };

/**
 * The permission checkbox matrix, shared by roles and users. Accordion groups, one open at a time; a row per
 * page with a checkbox per action and an All/None toggle. Any write ticks View; unticking View clears the row.
 * `locked` actions (granted elsewhere, e.g. by the role) show ticked and cannot be changed here. `toolbar={false}` hides the
 * bulk buttons, for editors whose map should stay small (a user's add-only overrides). Developments first lists the
 * development hub's tabs (registry.developmentTabs) with the rows behind each — the same keys, so a tick shows everywhere.
 */
export function PermissionMatrix({ registry, value, onChange, disabled = false, locked, templates, toolbar = true }: {
  registry: Registry; value: PermissionsMap; onChange: (next: PermissionsMap) => void; disabled?: boolean; locked?: PermissionsMap; templates?: PermissionTemplate[]; toolbar?: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const isLocked = (page: string, a: string) => has(locked, page, a);
  const on = (page: string, a: string) => isLocked(page, a) || has(value, page, a);

  const writePage = (page: RegistryPage, access: Record<string, boolean | number>) => onChange({ ...value, [page.key]: access });
  /** A limit box (beside approve): empty = not set here (the role's number, or 0). */
  const setLimit = (page: RegistryPage, key: string, raw: string) => {
    const next = expandPage(page, value[page.key]);
    const n = raw.trim() === '' ? undefined : Math.max(0, Number(raw));
    if (n === undefined || Number.isNaN(n)) delete next[key]; else next[key] = n;
    writePage(page, next);
  };

  const toggle = (page: RegistryPage, action: string) => {
    const next = { ...expandPage(page, value[page.key]), [action]: !has(value, page.key, action) };
    if (action === 'read' && !next.read) for (const a of page.actions) next[a] = false;
    else if (action !== 'read' && next[action] && page.actions.includes('read')) next.read = true;
    writePage(page, next);
  };
  const setRow = (page: RegistryPage, all: boolean) => writePage(page, Object.fromEntries(page.actions.map((a) => [a, all])));

  const byKey = new Map(registry.pages.map((p) => [p.key, p]));
  /** One page's row: a checkbox per action and an All/None toggle. Under a hub tab, `sub` is the sub-tab it sits in and a
   *  page from another group says where else it is listed — the same key, so ticking it here ticks it there. */
  const row = (p: RegistryPage, inGroup: string, sub?: string | null) => {
    const actions = ordered(p.actions);
    const allOn = actions.every((a) => on(p.key, a));
    const editable = actions.some((a) => !isLocked(p.key, a));
    return (
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-3 py-2 hover:bg-ink-50/60">
        <div className={`min-w-[140px] text-[12.5px] ${sub ? 'pl-3' : ''}`}>{sub && <span className="mr-1.5 text-[11px] text-ink-400">{sub} ·</span>}<span className="font-medium text-ink-800">{p.label}</span>{p.region && <span className="ml-1.5 text-[10.5px] uppercase text-ink-400">{p.region}</span>}{p.group !== inGroup && <span className="ml-1.5 text-[11px] text-ink-400">also under {p.group}</span>}</div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {actions.map((a) => {
            const lockedHere = isLocked(p.key, a); const checked = on(p.key, a);
            return (
              <span key={a} className="flex items-center gap-1.5">
              <label title={lockedHere ? 'Granted by the role' : undefined} className={`flex items-center gap-1.5 select-none ${lockedHere || disabled ? 'cursor-default' : 'cursor-pointer'}`}>
                <input type="checkbox" checked={checked} disabled={disabled || lockedHere} onChange={() => toggle(p, a)} className="h-3.5 w-3.5 accent-bronze-500" />
                <span className={`inline-flex items-center gap-1 text-[12px] ${lockedHere ? 'text-ink-400' : checked ? 'text-ink-800' : 'text-ink-400'}`}>{lockedHere && <Lock className="h-2.5 w-2.5" />}{actionLabel(a)}</span>
              </label>
                {/* A page's numeric limits sit beside approve, e.g. "up to 5% below the advert". */}
                {a === 'approve' && (p.limits ?? []).map((l) => {
                  const own = limitOf(value, p.key, l.key); const fromRole = limitOf(locked, p.key, l.key);
                  return (
                    <span key={l.key} title={l.label} className="inline-flex items-center gap-1">
                      <input type="number" min={0} step={0.5} inputMode="decimal" aria-label={l.label} disabled={disabled}
                        value={own ?? ''} placeholder={fromRole !== undefined ? String(fromRole) : '0'}
                        onChange={(e) => setLimit(p, l.key, e.target.value)}
                        className="h-6 w-14 rounded border border-ink-200 bg-white px-1.5 text-right text-[12px] text-ink-800 focus:border-bronze-500 focus:outline-none disabled:bg-ink-50 disabled:text-ink-400" />
                      <span className="text-[11px] text-ink-500">{l.unit === '%' ? '% below advert' : '£'}</span>
                    </span>
                  );
                })}
              </span>
            );
          })}
          <div className="flex min-w-[3rem] justify-end">
            {!disabled && editable && (
              <Button type="button" size="sm" variant="secondary" className="!h-6 !px-2.5 !text-[11px]" onClick={() => setRow(p, !allOn)}>{allOn ? 'None' : 'All'}</Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2.5">
      {!disabled && toolbar && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {templates && templates.length > 0 && (
            <Select defaultValue="" title="Pre-fill the matrix, then tweak per page"
              onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); e.currentTarget.value = ''; if (t) onChange(explicitMap(registry, t.build())); }}
              className="!h-7 max-w-full truncate text-[12px] sm:w-48">
              <option value="" disabled>Apply template…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </Select>
          )}
          <Button type="button" size="sm" variant="secondary" onClick={() => onChange(explicitMap(registry, 'read'))}>Read-only</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => onChange(explicitMap(registry, 'all'))}>Grant all</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => onChange(explicitMap(registry, 'none'))}>Revoke all</Button>
        </div>
      )}

      <div className="space-y-2">
        {registry.groups.map((g) => {
          const pages = registry.pages.filter((p) => p.group === g);
          if (!pages.length) return null;
          const isOpen = open === g;
          // Developments also lists the hub's tabs, each with the pages behind it; the count takes each page key once.
          const hub = g === 'Developments' ? (registry.developmentTabs ?? []) : [];
          const hubKeys = new Set(hub.flatMap((t) => t.pages));
          const counted = [...new Map([...pages, ...[...hubKeys].map((k) => byKey.get(k)).filter((p): p is RegistryPage => !!p)].map((p) => [p.key, p])).values()];
          const total = counted.reduce((n, p) => n + p.actions.length, 0);
          const granted = counted.reduce((n, p) => n + p.actions.filter((a) => on(p.key, a)).length, 0);
          const rest = hub.length ? pages.filter((p) => !hubKeys.has(p.key)) : pages;
          return (
            <div key={g} className="overflow-hidden rounded-lg border border-ink-200">
              <button type="button" onClick={() => setOpen(isOpen ? null : g)} className="flex w-full items-center gap-2 bg-ink-50 px-3 py-2 text-left transition hover:bg-ink-100">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">{g}</span>
                <span className={`text-[11px] ${granted ? 'text-bronze-700' : 'text-ink-400'}`}>{granted}/{total}</span>
                <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-ink-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="divide-y divide-ink-100 border-t border-ink-100">
                  {hub.length > 0 && (
                    <div className="bg-ink-50/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-500">Development hub tabs</div>
                  )}
                  {hub.map((t) => {
                    // Tabs with sub-tabs list each sub-tab's pages under its label; the rest list their pages straight.
                    const parts = t.subTabs?.length ? t.subTabs : [{ label: null as string | null, pages: t.pages }];
                    return (
                      <div key={t.key} className="divide-y divide-ink-100">
                        <div className="px-3 pb-1 pt-2 text-[12.5px] font-semibold text-ink-800">{t.label}</div>
                        {parts.flatMap((s) => s.pages.map((k) => byKey.get(k)).filter((p): p is RegistryPage => !!p).map((p) => (
                          <div key={`${t.key}-${s.label ?? ''}-${p.key}`}>{row(p, g, s.label)}</div>
                        )))}
                      </div>
                    );
                  })}
                  {hub.length > 0 && rest.length > 0 && (
                    <div className="bg-ink-50/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-500">Other</div>
                  )}
                  {rest.map((p) => <div key={p.key}>{row(p, g)}</div>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
